import { issueReasonUrl, issueClassificationUrl } from "./constants";
import base from "../middlewares/interceptors";
import { IIssueReasonPayload } from "../types/Reason";

/**
 * Get all issue classifications for dropdown (without pagination)
 */
export const getAllIssueClassifications = async () => {
  try {
    const response = await base.get(`${issueClassificationUrl}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching all issue classifications:", error);
    throw error;
  }
};

/**
 * Get all issue reasons for dropdown (without pagination)
 */
export const getAllIssueReasons = async () => {
  try {
    const response = await base.get(`${issueReasonUrl}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching all issue reasons:", error);
    throw error;
  }
};

/**
 * Get reasons filtered by classification ID
 * Route: /issue-reason?issue_classification_id={classificationId}
 */
export const getReasonsByClassificationId = async (
  classificationId: number
) => {
  try {
    const response = await base.get(
      `${issueReasonUrl}?issue_classification_id=${classificationId}`
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching reasons by classification ID:",
      error
    );
    throw error;
  }
};

/**
 * Get reason by ID
 */
export const getIssueReasonById = async (reasonId: string) => {
  try {
    const response = await base.get(`${issueReasonUrl}/${reasonId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching reason by ID:", error);
    throw error;
  }
};

/**
 * Add new reason
 */
export const addIssueReason = async ({
  payload,
}: {
  payload: IIssueReasonPayload;
}) => {
  try {
    const response = await base.post(`${issueReasonUrl}`, payload);
    return response.data;
  } catch (error) {
    console.error("Error adding reason:", error);
    throw error;
  }
};

/**
 * Update reason
 */
export const updateIssueReason = async ({
  currentReasonId,
  payload,
}: {
  currentReasonId: string;
  payload: IIssueReasonPayload;
}) => {
  try {
    const response = await base.put(
      `${issueReasonUrl}/${currentReasonId}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating reason:", error);
    throw error;
  }
};

/**
 * Delete reason
 */
export const deleteIssueReason = async (reasonId: string) => {
  try {
    const response = await base.delete(`${issueReasonUrl}/${reasonId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting reason:", error);
    throw error;
  }
};

/**
 * Reactivate reason
 */
export const reactivateIssueReason = async (reasonId: string) => {
  try {
    const response = await base.put(`${issueReasonUrl}/${reasonId}/reactivate`);
    return response.data;
  } catch (error) {
    console.error("Error reactivating reason:", error);
    throw error;
  }
};

/**
 * Get paginated issue reasons list
 */
export const getPaginatedIssueReasons = async (
  page: number = 1,
  size: number = 25,
  searchTerm?: string,
  signal?: AbortSignal,
  filterIds?: string,
  isActive?: boolean
) => {
  try {
    let url = `${issueReasonUrl}/pages?page=${page}&size=${size}`;

    if (filterIds && filterIds.trim().length > 0) {
      url += `&issue_reason_filter_ids=${encodeURIComponent(filterIds.trim())}`;
    }

    if (searchTerm && searchTerm.trim().length >= 3) {
      url += `&reason_name=${encodeURIComponent(searchTerm.trim())}`;
    }

    if (isActive !== undefined) {
      url += `&is_active=${isActive}`;
    }

    const response = await base.get(url, { signal });
    return response.data;
  } catch (error) {
    console.error("Error fetching paginated issue reasons:", error);
    throw error;
  }
};

